/**
 * Puts each node's stored topic back before a mind map is saved. Kept apart from the DOM so it can be tested alone.
 * A viewer who translates sees translated topics on screen, and the map they save must keep the stored ones.
 */

export interface MindmapNode {
    id?: string;
    topic?: string;
    children?: MindmapNode[];
}

/** The stored topic of every node by id, as MindmapUtil::getNodeTopics() renders them into data-node-topics. */
export type NodeTopics = Record<string, string>;

/** The stored topic of each text on screen, or null when the text stands for two different stored topics. */
export type OriginalsByShownTopic = Map<string, string | null>;

/** Adds the text on screen and the stored topic of root and every known node under it to originals. */
export const rememberShownTopics = (
    root: MindmapNode | null | undefined,
    nodeTopics: NodeTopics,
    originals: OriginalsByShownTopic,
): void => {
    if (!root) {
        return;
    }
    const original = nodeTopics[root.id];
    if (original !== undefined) {
        const seen = originals.get(root.topic);
        originals.set(root.topic, seen === undefined || seen === original ? original : null);
    }
    (root.children || []).forEach(child => rememberShownTopics(child, nodeTopics, originals));
};

/**
 * Sets the topic of root and every node under it to the stored topic.
 *
 * A copied or pasted node gets a new id that is not in nodeTopics and keeps the text on screen,
 * so it takes the original of a node showing the same text, on the map now or remembered when it was copied or cut.
 * Text shown for two originals stays as is.
 */
export const restoreOriginalTopics = (
    root: MindmapNode | null | undefined,
    nodeTopics: NodeTopics,
    remembered?: OriginalsByShownTopic,
): void => {
    if (!root) {
        return;
    }

    const originalsByShownTopic: OriginalsByShownTopic = new Map(remembered);
    rememberShownTopics(root, nodeTopics, originalsByShownTopic);

    const setOriginalTopic = (node: MindmapNode): void => {
        const originalTopic = nodeTopics[node.id] === undefined
            ? originalsByShownTopic.get(node.topic)
            : nodeTopics[node.id];
        if (originalTopic) {
            node.topic = originalTopic;
        }
        (node.children || []).forEach(setOriginalTopic);
    };

    setOriginalTopic(root);
};
